import React, { useMemo, useState } from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

const ResetPassword = () => {
  const apiBaseUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v3`;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [status, setStatus] = useState("idle"); // idle | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPasswordValue = watch("newPassword");

  const onSubmit = async ({ newPassword }) => {
    setErrorMessage("");
    setStatus("idle");
    try {
      const res = await fetch(`${apiBaseUrl}/auth/password-reset/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, newPassword }),
      });
      if (!res.ok) {
        throw new Error("The reset link is invalid or expired.");
      }
      setStatus("success");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.message || "Unable to reset password. Please try again.");
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Controller
        name="newPassword"
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField
            type="password"
            variant="outlined"
            required
            fullWidth
            onChange={onChange}
            value={value}
            id="newPassword"
            label="New Password"
            placeholder="Enter new password"
            autoComplete="new-password"
            error={!!errors.newPassword}
            helperText={errors.newPassword && "Password must be at least 8 characters"}
          />
        )}
        {...register("newPassword", { required: true, minLength: 8 })}
        ref={null}
      />

      <Box mt={2}>
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="password"
              variant="outlined"
              required
              fullWidth
              onChange={onChange}
              value={value}
              id="confirmPassword"
              label="Confirm New Password"
              placeholder="Re-enter new password"
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword && "Passwords must match"}
            />
          )}
          {...register("confirmPassword", {
            required: true,
            validate: (value) => value === newPasswordValue,
          })}
          ref={null}
        />
      </Box>

      <Box my={2}>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={!isValid || isSubmitting || !token}
        >
          Reset password
        </Button>
      </Box>
      {errorMessage && (
        <Typography color="error" align="center">
          {errorMessage}
        </Typography>
      )}
      {!token && (
        <Typography color="error" align="center">
          Reset token missing. Please use the link from your email.
        </Typography>
      )}
    </form>
  );

  return (
    <section>
      <Container maxWidth="sm">
        <Box pt={8} pb={10}>
          <Box mb={6} textAlign="center">
            <Typography variant="h4" component="h2" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Enter your new password below.
            </Typography>
          </Box>

          {status === "success" ? (
            <Box textAlign="center">
              <Typography variant="h6" gutterBottom>
                Your password has been reset.
              </Typography>
              <Typography color="textSecondary">
                Redirecting you to the login page...
              </Typography>
            </Box>
          ) : (
            renderForm()
          )}
        </Box>
      </Container>
    </section>
  );
};

export default ResetPassword;
